import { Trash2 } from 'lucide-react-native';
import { ComponentType, ReactNode } from 'react';
import { StyleSheet, Text, View, Platform } from 'react-native';
import { Gesture, GestureDetector } from 'react-native-gesture-handler';
import Animated, {
  Easing,
  runOnJS,
  useAnimatedStyle,
  useSharedValue,
  withTiming,
} from 'react-native-reanimated';

import { Fonts, Palette, Radius } from '@/constants/theme';

/**
 * SwipeRow — wrap any row child with a horizontal-pan gesture that
 * reveals an action label on the LEFT and fires the action when the
 * user swipes past a threshold. Used on the inbox today; same shape
 * can wrap any other list row (tasks, contacts, etc.).
 *
 * Design choices:
 *   - Right-swipe direction (translateX positive). Standard iOS/email
 *     "swipe right to delete" pattern — feels native on phones.
 *   - Reveal threshold = 80px. Below that the row springs back; at or
 *     past it, the row continues out and onAction fires.
 *   - The action label sits BEHIND the row, fixed to the left edge.
 *     As the row slides right, more of the label is revealed — same
 *     mechanic the iOS Mail.app uses.
 *   - Defaults to a clay-tinted "Delete" with a trash icon. Override
 *     via `actionLabel`, `actionIcon`, `actionTint`.
 *
 * Web note: gesture-handler's Pan works on web but with some quirks
 * around inertial scroll. We use `activeOffsetX` to require a clear
 * horizontal intent before swallowing the gesture, so vertical
 * scrolling through the inbox isn't accidentally swiped.
 */

const SWIPE_THRESHOLD = 80;

interface Props {
  children: ReactNode;
  onAction: () => void;
  actionLabel?: string;
  actionIcon?: ComponentType<{ size?: number; color?: string; strokeWidth?: number }>;
  actionTint?: string;
  /** Disable the gesture (e.g. when the row is in a select / drag mode). */
  disabled?: boolean;
}

export function SwipeRow({
  children,
  onAction,
  actionLabel = 'Delete',
  actionIcon: ActionIcon = Trash2,
  actionTint = Palette.clay,
  disabled,
}: Props) {
  const translateX = useSharedValue(0);

  const finish = () => {
    onAction();
  };

  const pan = Gesture.Pan()
    .enabled(!disabled)
    // Require a clear horizontal intent before swallowing the gesture
    // so list scrolling stays smooth. ~12px of horizontal motion has
    // to happen before this pan activates.
    .activeOffsetX([-12, 12])
    .failOffsetY([-10, 10])
    .onUpdate((e) => {
      // Only follow rightward swipes; clamp leftward to 0.
      translateX.value = Math.max(0, e.translationX);
    })
    .onEnd((e) => {
      if (e.translationX >= SWIPE_THRESHOLD) {
        // Continue the slide out to the right, then fire the action.
        translateX.value = withTiming(
          400,
          { duration: 200, easing: Easing.in(Easing.cubic) },
          () => {
            runOnJS(finish)();
          }
        );
      } else {
        // Spring back to rest.
        translateX.value = withTiming(0, {
          duration: 200,
          easing: Easing.out(Easing.cubic),
        });
      }
    });

  const cardStyle = useAnimatedStyle(() => ({
    transform: [{ translateX: translateX.value }],
  }));

  const labelStyle = useAnimatedStyle(() => ({
    // Fade the label in once the user has moved past a small threshold.
    opacity: Math.min(1, translateX.value / SWIPE_THRESHOLD),
  }));

  // Web: react-native-gesture-handler's Pan still works but feels less
  // responsive than on native. We still wrap because tests on web showed
  // it functional; if it ever proves janky we can early-return the
  // children unwrapped on Platform.OS === 'web'.
  if (Platform.OS === 'web' && disabled) {
    return <>{children}</>;
  }

  return (
    <View style={styles.wrap}>
      <Animated.View style={[styles.actionBg, { backgroundColor: actionTint }, labelStyle]}>
        <ActionIcon size={16} color={Palette.cream} strokeWidth={2} />
        <Text style={styles.actionText}>{actionLabel}</Text>
      </Animated.View>
      <GestureDetector gesture={pan}>
        <Animated.View style={cardStyle}>{children}</Animated.View>
      </GestureDetector>
    </View>
  );
}

const styles = StyleSheet.create({
  wrap: {
    position: 'relative',
    overflow: 'hidden',
    borderRadius: Radius.lg,
  },
  actionBg: {
    position: 'absolute',
    top: 0, bottom: 0, left: 0,
    width: 160,
    flexDirection: 'row',
    alignItems: 'center',
    gap: 8,
    paddingLeft: 18,
    borderTopLeftRadius: Radius.lg,
    borderBottomLeftRadius: Radius.lg,
  },
  actionText: {
    fontFamily: Fonts.sansBold,
    fontSize: 13,
    color: Palette.cream,
    letterSpacing: 0.3,
  },
});
